// QoE beacon emitter — plan A2 (havik-streams docs/plans/havik-analytics-plan.md).
//
// One AnalyticsSession per playback session (per sid). Wire format is the
// proto3 JSON mapping of `havik_analytics.BeaconBatch` (protocol-buffers
// repo): camelCase keys, enums by NAME, and — the one contract landmine —
// StateChange uses `fromState`/`toState` (`from` is a Python keyword; the
// contract renamed the pair for every generator).
//
// Privacy posture (plan D11, asserted by test): the sid arrives in the
// playback response, lives in this object, and dies with it. This module
// never touches localStorage, sessionStorage, cookies, or IndexedDB.
//
// Delivery is fire-and-forget (plan D3/D9): beacons are dashboard-grade, the
// CDN edge-log plane is the billing truth, so a lost batch is dropped —
// never retried into a stampede, never allowed to affect playback.

import { type SessionEnvironment } from './environment';
import { type PlaybackStats, type PlayerState } from './stats';

/** Beacon cadence — plan §9 settled at review time to 15s. */
const DEFAULT_HEARTBEAT_MS = 15_000;
/**
 * How long a session may sit PAUSED before it closes itself, and the
 * suspension gap (no timer fired for this long: OS sleep, a frozen tab) after
 * which it does the same on waking.
 *
 * Without this a session had exactly four ends — pagehide, stream end,
 * destroy(), fatal error — so a viewer who paused and forgot the tab, closed
 * the lid, or had the tab discarded left a session "still open" for hours
 * (portal, 2026-09-19: paused heartbeats from 16:13 on, no end), and a slept
 * laptop booked its whole sleep as playing time on waking. Ten minutes is
 * longer than any pause that is still "the same viewing" (an ad break, a
 * phone call) and shorter than half-time; a viewer who comes back after it
 * starts a NEW session (the player reloads on the next play()), which is
 * what it is. Hidden-but-playing is NOT idle: audio keeps playing in a
 * background tab.
 */
const DEFAULT_IDLE_MS = 10 * 60_000;
/**
 * The suspension detector's floor, whatever idleMs is. A gap between two of
 * our ticks is only a suspension when no legitimate cadence explains it: a
 * hidden tab's throttled timers gap to a minute (Chrome, after 5 min
 * hidden), and a host-configured idleMs below the heartbeat interval is a
 * PAUSE limit, not a licence to close a playing session on its first
 * ordinary heartbeat. Five minutes is above every throttled cadence seen and
 * below any sleep worth closing over.
 */
const MIN_SUSPENSION_GAP_MS = 5 * 60_000;
/** Queue bound: past this the oldest non-lifecycle beacons drop first. */
const MAX_PENDING = 100;
/** Startup stages report >=1ms when reached; 0 means never reached. */
const MIN_STAGE_MS = 1;
/**
 * How long SessionStart waits for the autoplay verdict before going out.
 *
 * The contract puts the autoplay result on SessionStart, but the engine is
 * committed (and this session constructed) BEFORE play() is ever called, so
 * at enqueue time the verdict does not exist yet. Holding the first flush
 * for a beat lets the real value ride the beacon it belongs on; nothing is
 * lost if the verdict misses the window, and any earlier flush (an error,
 * end, pagehide) sends immediately regardless.
 */
const FIRST_FLUSH_DELAY_MS = 1_000;
/** A stall or error this close to the end of a session is what the viewer
 *  left over — beyond it, the two are unrelated events in one session. */
const ABANDON_WINDOW_MS = 30_000;
/**
 * How long after a seek a stall is still the seek's.
 *
 * Was an inline 1_000 in causeOf; named because the rebuffer counters now
 * share it. A seek re-buffers from a new position, so the gap is bounded by
 * a segment fetch rather than by anything the viewer did — one second covers
 * it at any realistic segment duration without swallowing a genuine stall
 * that merely happens to follow a seek.
 */
const SEEK_STALL_WINDOW_MS = 1_000;

/** Contract enum names (proto3 JSON encodes enums by name). */
type WirePlayer = 'PLAYER_HLSJS' | 'PLAYER_SAFARI_NATIVE';
type WireState =
  | 'PLAYER_STATE_UNSPECIFIED'
  | 'PLAYER_STATE_IDLE'
  | 'PLAYER_STATE_WAITING'
  | 'PLAYER_STATE_LOADING'
  | 'PLAYER_STATE_PLAYING'
  | 'PLAYER_STATE_BUFFERING'
  | 'PLAYER_STATE_PAUSED'
  | 'PLAYER_STATE_ENDED'
  | 'PLAYER_STATE_ERROR';
type WireKeySystem = 'KEY_SYSTEM_NONE' | 'KEY_SYSTEM_WIDEVINE' | 'KEY_SYSTEM_FAIRPLAY';
type WirePresentation = 'PRESENTATION_INLINE' | 'PRESENTATION_FULLSCREEN' | 'PRESENTATION_PIP';
type WireCause =
  | 'TRANSITION_CAUSE_USER'
  | 'TRANSITION_CAUSE_SEEK'
  | 'TRANSITION_CAUSE_NETWORK_STALL'
  | 'TRANSITION_CAUSE_ERROR'
  | 'TRANSITION_CAUSE_AUTO';
type WireErrorStage =
  | 'ERROR_STAGE_RESOLVE'
  | 'ERROR_STAGE_MANIFEST'
  | 'ERROR_STAGE_DRM'
  | 'ERROR_STAGE_FIRST_SEGMENT'
  | 'ERROR_STAGE_SEGMENT'
  | 'ERROR_STAGE_DECODE'
  | 'ERROR_STAGE_OTHER';
export type WireAutoplay =
  | 'AUTOPLAY_RESULT_ALLOWED'
  | 'AUTOPLAY_RESULT_ALLOWED_MUTED'
  | 'AUTOPLAY_RESULT_BLOCKED'
  | 'AUTOPLAY_RESULT_NOT_ATTEMPTED';
/** Presentation the viewer is watching in, as the player reports it. */
export type Presentation = 'inline' | 'fullscreen' | 'pip';
const PRESENTATION_TO_WIRE: Record<Presentation, WirePresentation> = {
  inline: 'PRESENTATION_INLINE',
  fullscreen: 'PRESENTATION_FULLSCREEN',
  pip: 'PRESENTATION_PIP',
};

type WireEndReason =
  | 'END_REASON_ENDED'
  | 'END_REASON_PAGEHIDE'
  | 'END_REASON_STOPPED'
  | 'END_REASON_FATAL_ERROR'
  | 'END_REASON_IDLE';

const KEY_SYSTEM_TO_WIRE: Record<'widevine' | 'fairplay' | 'none', WireKeySystem> = {
  widevine: 'KEY_SYSTEM_WIDEVINE',
  fairplay: 'KEY_SYSTEM_FAIRPLAY',
  none: 'KEY_SYSTEM_NONE',
};

/**
 * Nearest-rank percentile over an ascending slice; 0 for an empty one.
 *
 * Nearest rank, not interpolation: the value returned is one the session
 * actually observed. The rank is ceil(p/100 * N), so p50 of four samples
 * is the second, not the third — worth stating because the two conventions
 * differ only at exact boundaries and a future reader is otherwise likely
 * to "fix" one into the other.
 */
function percentile(ascending: number[], p: number): number {
  if (ascending.length === 0) return 0;
  const rank = Math.min(ascending.length, Math.max(1, Math.ceil((p / 100) * ascending.length)));
  return ascending[rank - 1] ?? 0;
}

/** Codes raised by /v1/playback before any media is fetched. */
const RESOLVE_CODES = new Set([
  'INVALID_URN',
  'NOT_FOUND',
  'GONE',
  'TOO_EARLY',
  'UNAVAILABLE',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'RATE_LIMITED',
  'TIMEOUT',
]);

const STATE_TO_WIRE: Record<PlayerState, WireState> = {
  idle: 'PLAYER_STATE_IDLE',
  waiting: 'PLAYER_STATE_WAITING',
  loading: 'PLAYER_STATE_LOADING',
  playing: 'PLAYER_STATE_PLAYING',
  buffering: 'PLAYER_STATE_BUFFERING',
  paused: 'PLAYER_STATE_PAUSED',
  ended: 'PLAYER_STATE_ENDED',
  error: 'PLAYER_STATE_ERROR',
};

interface WireSessionStart extends Partial<SessionEnvironment> {
  player: WirePlayer;
  sdkVersion: string;
  autoplay?: WireAutoplay;
}

interface WireBeacon {
  sid: string;
  clientTime: string;
  seq: number;
  sessionStart?: WireSessionStart;
  mediaInfo?: {
    videoCodec?: string;
    audioCodec?: string;
    keySystem?: WireKeySystem;
    drmSecurityLevel?: string;
    ladderHeights?: number[];
    ladderTopBitrateKbps?: number;
    lowLatency?: boolean;
  };
  heartbeat?: Record<string, number | string | boolean>;
  stateChange?: {
    fromState: WireState;
    toState: WireState;
    positionMs?: number;
    renditionHeight?: number;
    cause?: WireCause;
    presentation?: WirePresentation;
  };
  error?: {
    code: string;
    detail?: string;
    fatal?: boolean;
    httpStatus?: number;
    recoveryAttempts?: number;
    recovered?: boolean;
    positionMs?: number;
    stage?: WireErrorStage;
    cdnHost?: string;
  };
  sessionEnd?: Record<string, number | string>;
}

/** What the player hands the session at each heartbeat. */
export type AnalyticsSample = PlaybackStats & {
  state: PlayerState;
  positionMs: number;
  /** Forward buffer depth, which only the player can measure. */
  bufferedMs?: number;
  /** >1 means the engine is catching up toward the live edge. */
  playbackRate?: number;
};

export interface AnalyticsSessionOptions {
  /** Beacons ingest base URL — POSTs go to `<endpoint>/v1/beacons`. */
  endpoint: string;
  /** The per-session id minted by havik-streams at resolve time. */
  sid: string;
  player: WirePlayer;
  sdkVersion: string;
  /**
   * Called at each heartbeat for the current cumulative stats; the session
   * computes per-interval deltas itself.
   */
  sample: () => AnalyticsSample;
  /**
   * Where the session runs (contract v2): form factor, device model, OS
   * version, screen, connection, embedding host. Collected once by the
   * player. Model and OS version come from UA client hints and are absent
   * on browsers that do not implement them; no IP and no raw user agent
   * ever leave the client (see core/environment).
   */
  environment?: SessionEnvironment;
  heartbeatMs?: number;
  /**
   * Idle limit; see DEFAULT_IDLE_MS. `0` disables the idle close entirely.
   * The suspension detector never fires below MIN_SUSPENSION_GAP_MS,
   * whatever this is set to.
   */
  idleMs?: number;
  /**
   * Called once, after the session ended itself for inactivity
   * (END_REASON_IDLE) — the player latches "reload on the next play()" on it,
   * because this sid is finished and whatever plays next is a new session.
   */
  onIdle?: () => void;
  /** Test seams. */
  now?: () => number;
  sendBeacon?: (url: string, body: string) => boolean;
  fetchFn?: typeof fetch;
}

/**
 * Derive the beacons ingest base from the streams API base by the platform
 * hostname convention (`feed[-dev].<domain>` → `beacons[-dev].<domain>`),
 * mirroring how the SSE endpoint derives `events.<domain>`. Returns
 * undefined when the convention doesn't apply — the caller must then pass
 * an explicit endpoint for beacons to flow.
 */
export function deriveBeaconsBaseUrl(baseUrl: string): string | undefined {
  try {
    const url = new URL(baseUrl);
    const [first, ...rest] = url.hostname.split('.');
    if (!first || rest.length === 0) return undefined;
    if (first !== 'feed' && !first.startsWith('feed-')) return undefined;
    url.hostname = [first.replace(/^feed/, 'beacons'), ...rest].join('.');
    url.pathname = '';
    url.search = '';
    return url.origin;
  } catch {
    return undefined;
  }
}

/** Causes already warned about, so each console line is printed once. */
const warnedBeaconCauses = new Set<string>();

/** What {@link resolveBeaconsEndpoint} decided, and why. */
export interface BeaconsEndpointResolution {
  /** Where beacons go. Undefined means delivery is OFF for this session. */
  endpoint?: string;
  /**
   * Set whenever delivery is off for a reason the integrator can fix — a
   * ready-to-surface sentence naming the cause. Always populated in that case
   * (the caller emits it on every session, so a host that attaches a listener
   * late still hears about it); the console line it also triggers is deduped
   * per cause.
   */
  disabledReason?: string;
}

/** Disable delivery with a reason: said once on the console, returned always. */
function beaconsDisabled(cause: string, reason: string): BeaconsEndpointResolution {
  if (!warnedBeaconCauses.has(cause)) {
    warnedBeaconCauses.add(cause);
    console.warn(`havik-player: ${reason}`);
  }
  return { disabledReason: reason };
}

/**
 * Decide where this session's beacons go, and make a silent disable loud.
 *
 * The derivation convention is deliberately narrow (see
 * {@link deriveBeaconsBaseUrl}), so an integrator fronting the streams API
 * with their own hostname — a CDN in front of `feed.<domain>`, say — gets no
 * endpoint and, before this, no beacons and no word of it. That failure is
 * invisible from the client: playback is perfect, only the analytics plane is
 * empty, and it stays empty until someone asks the warehouse why a client has
 * descriptor mints and zero sessions. It cost ~a week of QoE data for one
 * client in Sept 2026.
 *
 * Widening the convention is NOT the fix (a guessed beacons host is a silent
 * 404 instead of a silent nothing) — saying so is.
 */
export function resolveBeaconsEndpoint(
  baseUrl: string,
  explicitEndpoint?: string,
): BeaconsEndpointResolution {
  if (explicitEndpoint) return { endpoint: explicitEndpoint };

  // Supplied but EMPTY — an unset env var, or a template that resolved to
  // nothing. Test truthiness, not `!== undefined`, but do not fall through to
  // derivation: the caller asked for a specific endpoint, and quietly
  // substituting a derived host would start delivery to somewhere they never
  // named. So this stays disabled, exactly as it was before this function
  // existed (`explicit ?? derive(...)` kept the '' and failed the `!endpoint`
  // check). What changes is that it no longer happens in silence.
  if (explicitEndpoint !== undefined) {
    return beaconsDisabled(
      'empty-endpoint',
      `analytics is disabled: no QoE beacons will be sent. analytics.endpoint ` +
        `was supplied but empty. Pass a real beacons origin, ` +
        `analytics: { endpoint: 'https://beacons[-dev].<domain>' }, or omit the ` +
        `option to derive it from baseUrl (or analytics: false to opt out).`,
    );
  }

  const derived = deriveBeaconsBaseUrl(baseUrl);
  if (derived) return { endpoint: derived };

  // Name the host, not the whole baseUrl: it is the part the convention
  // rejected, and it cannot carry a query string into the console.
  let host: string;
  try {
    host = new URL(baseUrl).hostname || baseUrl;
  } catch {
    host = baseUrl; // not a URL at all — echo it back, that IS the problem
  }
  return beaconsDisabled(
    host,
    `analytics is disabled: no QoE beacons will be sent. The beacons endpoint ` +
      `cannot be derived from the baseUrl host "${host}" — the convention is ` +
      `feed[-dev].<domain> → beacons[-dev].<domain>. Pass an explicit endpoint, ` +
      `analytics: { endpoint: 'https://beacons[-dev].<domain>' }, to createPlayer ` +
      `(or analytics: false to opt out deliberately).`,
  );
}

/**
 * One playback session's QoE beacon emitter. Construct after engine
 * selection (player identity is only truthful then), call lifecycle hooks
 * from the player, and end() exactly once — later calls are no-ops.
 */
export class AnalyticsSession {
  private readonly opts: Required<Pick<AnalyticsSessionOptions, 'heartbeatMs' | 'idleMs'>> &
    AnalyticsSessionOptions;
  private readonly url: string;
  private readonly now: () => number;

  private seq = 0;
  private pending: WireBeacon[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private endedFlag = false;

  // Delta bases for the cumulative counters sampled at each heartbeat.
  private lastBytes = 0;
  private lastPlaylistBytes = 0;
  private lastDropped = 0;
  private lastSwitches = 0;

  // Wall-clock accounting: playing/hidden time accumulate between their
  // state flips; totals close out at end().
  private playingSinceMs: number | null = null;
  private hiddenSinceMs: number | null = null;
  private playingTotalMs = 0;
  private hiddenTotalMs = 0;
  private lastHbPlayingMs = 0;
  private lastHbHiddenMs = 0;
  private lastHbRebufferMs = 0;
  private lastHbRebufferCount = 0;
  private lastHbActiveMs = 0;

  // Session totals for SessionEnd.
  private rebufferCount = 0;
  private rebufferTotalMs = 0;
  private bufferingSinceMs: number | null = null;
  // Rebuffer, like active watch, is a visible-only window: a backgrounded tab
  // is throttled into 'buffering' and would otherwise book minutes of stall the
  // viewer never saw. isBuffering is the state; bufferingSinceMs is the open
  // window, held only while isBuffering && !isHidden (see syncBuffering).
  private isBuffering = false;
  private errorCount = 0;
  private seekCount = 0;
  private pauseCount = 0;

  // ---- Contract v2 accounting.
  // Active watch = playing AND visible AND unmuted. Kept as its own window
  // rather than derived at read time, because the three flip independently
  // and a server can only ever reconstruct a lower bound from the deltas.
  private isPlaying = false;
  private isHidden = false;
  private isMuted = false;
  private activeSinceMs: number | null = null;
  private activeTotalMs = 0;
  // Muted time is counted only WHILE PLAYING, so it is subtractable from
  // playing time; a muted paused tab is not watch time to discount.
  private mutedSinceMs: number | null = null;
  private mutedTotalMs = 0;
  private lastHbMutedMs = 0;
  private volumeChanges = 0;
  private muteChanges = 0;
  // Presentation: which surface the viewer watched in, and for how long.
  private presentation: Presentation = 'inline';
  private presentationSinceMs: number;
  private fullscreenTotalMs = 0;
  private pipTotalMs = 0;
  // Armed-before-live time: a viewer who waited five minutes for kickoff had
  // a different session from one who joined a running match.
  private waitingSinceMs: number | null = null;
  private waitingTotalMs = 0;
  // Startup and abandon signals.
  private firstAudioMs = 0;
  private timeToFirstStallMs = 0;
  private lastStallMs = 0;
  private lastErrorMs = 0;
  // −1 means "no seek yet": 0 is a real seek time when `now` starts at 0.
  private lastSeekMs = -1;
  /** True while the CURRENT stall is the seek's rather than the network's. */
  private stallIsSeekInduced = false;
  private lastErrorStage: WireErrorStage | null = null;
  private sawFirstBytes = false;
  private mediaInfoSignature = '';
  // Delta bases for the v2 cumulative counters.
  private lastUpshifts = 0;
  private lastDownshifts = 0;
  private lastDecoded = 0;
  private lastRequestCount = 0;
  private lastRequestErrors = 0;
  // The un-flushed SessionStart, held briefly so the autoplay verdict can
  // land on it (see FIRST_FLUSH_DELAY_MS).
  private pendingStart: WireSessionStart | null = null;
  private firstFlushTimer: ReturnType<typeof setTimeout> | null = null;
  // Idle close (see DEFAULT_IDLE_MS): the pause timer, and the last instant a
  // timer of ours actually ran — the suspension detector's reference.
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private lastTickMs = 0;

  // Startup breakdown (0 = stage never reached, plan contract).
  private readonly bornMs: number;
  /**
   * What the startup ladder measures FROM: the moment the viewer asked for
   * playback, not the moment the player was constructed.
   *
   * Defaults to bornMs, which is correct for autoplay — intent and
   * construction coincide there. It moves once, on the first play(), for the
   * autoplay-off path, where the player is built with the manifest parsed and
   * then sits behind a poster for as long as the viewer takes to click. That
   * dwell was being reported as startup LATENCY: a viewer who read the page
   * for thirty seconds produced a thirty-second "time to first frame", and
   * ops' own help text has always described the figure this now computes
   * ("from the player being asked to play to the first frame").
   *
   * Every stage shares it, or they stop summing: the ladder is
   * manifest -> first segment -> first frame measured from one origin.
   */
  private startupAnchorMs: number;
  private startupTimeMs = 0;
  private manifestLoadMs = 0;
  private firstFragLoadMs = 0;

  private readonly onPagehide = () => this.end('END_REASON_PAGEHIDE');
  /**
   * Page Lifecycle `freeze`: the browser is about to stop running this page
   * (a backgrounded tab after ~5 min in Chrome, ahead of a discard). Nothing
   * runs after it — a discard fires no pagehide — so what is queued goes out
   * now, by sendBeacon, the only delivery a stopping page guarantees. Not an
   * end: the page may `resume`, and the suspension check there decides.
   */
  private readonly onFreeze = () => {
    if (this.endedFlag) return;
    this.flush(true);
  };
  /** Page Lifecycle `resume`: the page runs again; was it away past the idle limit? */
  private readonly onResume = () => this.checkSuspension(this.now());
  private readonly onVisibility = () => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'hidden') {
      if (this.hiddenSinceMs === null) this.hiddenSinceMs = this.now();
      this.isHidden = true;
    } else {
      if (this.hiddenSinceMs !== null) {
        this.hiddenTotalMs += this.now() - this.hiddenSinceMs;
        this.hiddenSinceMs = null;
      }
      this.isHidden = false;
    }
    this.syncActive();
    this.syncBuffering();
  };

  constructor(opts: AnalyticsSessionOptions) {
    // ?? at the use point, not object spread: a caller passing an explicit
    // `heartbeatMs: undefined` must not clobber the default into a 0ms
    // busy-loop interval.
    this.opts = {
      ...opts,
      heartbeatMs: opts.heartbeatMs ?? DEFAULT_HEARTBEAT_MS,
      idleMs: opts.idleMs ?? DEFAULT_IDLE_MS,
    };
    this.url = `${opts.endpoint.replace(/\/$/, '')}/v1/beacons`;
    this.now = opts.now ?? (() => Date.now());
    this.bornMs = this.now();
    // Not a startup stage: presentation time is fullscreen/PiP accounting and
    // is measured over the session's whole life, so it keeps bornMs.
    this.presentationSinceMs = this.bornMs;
    this.startupAnchorMs = this.bornMs;
  }

  /** Emit SessionStart (always the session's first beacon) and arm timers. */
  start(): void {
    if (this.endedFlag) return;
    const sessionStart: WireSessionStart = {
      player: this.opts.player,
      sdkVersion: this.opts.sdkVersion,
      ...this.opts.environment,
    };
    this.pendingStart = sessionStart;
    this.enqueue({ sessionStart });
    // Not flushed here: the autoplay verdict does not exist yet. See
    // FIRST_FLUSH_DELAY_MS — noteAutoplay, an error, a heartbeat or end all
    // send earlier if they happen first.
    this.firstFlushTimer = setTimeout(() => this.flush(), FIRST_FLUSH_DELAY_MS);
    this.lastTickMs = this.now();
    this.timer = setInterval(() => this.heartbeat(), this.opts.heartbeatMs);
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.onPagehide);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('freeze', this.onFreeze);
      document.addEventListener('resume', this.onResume);
      this.onVisibility(); // seed hidden accounting if born in a hidden tab
    }
  }

  /**
   * Did the page stop running for at least the idle limit? Timers do not
   * fire through OS sleep or a frozen tab, so the gap between two of our own
   * ticks is the measure: a heartbeat that finds the clock jumped past the
   * limit is the first tick after a suspension, and the session it wakes
   * into is over — nothing was watched, and every open accounting window
   * (playing, active, muted…) would otherwise book the whole gap. Ends as
   * IDLE; the next play() starts a new session.
   */
  private checkSuspension(t: number): boolean {
    if (this.endedFlag || !this.opts.idleMs) return false;
    const gap = Math.max(this.opts.idleMs, MIN_SUSPENSION_GAP_MS);
    if (this.lastTickMs > 0 && t - this.lastTickMs >= gap) {
      this.endIdle(this.lastTickMs);
      return true;
    }
    this.lastTickMs = t;
    return false;
  }

  /** Arm (paused) or disarm (anything else) the idle close. */
  private syncIdle(state: PlayerState): void {
    if (state === 'paused' && this.opts.idleMs) {
      if (this.idleTimer === null) {
        this.idleTimer = setTimeout(() => {
          this.idleTimer = null;
          this.endIdle();
        }, this.opts.idleMs);
      }
    } else if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private endIdle(at?: number): void {
    if (this.endedFlag) return;
    this.end('END_REASON_IDLE', at);
    this.opts.onIdle?.();
  }

  /** Player state transition — also drives rebuffer/watch-time accounting. */
  noteStateChange(
    from: PlayerState,
    to: PlayerState,
    positionMs: number,
    renditionHeight?: number,
  ): void {
    if (this.endedFlag || from === to) return;
    const t = this.now();

    if (to === 'playing') {
      if (this.playingSinceMs === null) this.playingSinceMs = t;
      if (this.startupTimeMs === 0) {
        this.startupTimeMs = Math.max(MIN_STAGE_MS, t - this.startupAnchorMs);
      }
      if (this.firstAudioMs === 0 && !this.isMuted) {
        // The closest a browser gets to "first audible frame": playback
        // running with the element neither muted nor at zero volume.
        this.firstAudioMs = Math.max(MIN_STAGE_MS, t - this.startupAnchorMs);
      }
      if (this.isMuted && this.mutedSinceMs === null) this.mutedSinceMs = t;
    } else if (this.playingSinceMs !== null) {
      this.playingTotalMs += t - this.playingSinceMs;
      this.playingSinceMs = null;
      if (this.mutedSinceMs !== null) {
        this.mutedTotalMs += t - this.mutedSinceMs;
        this.mutedSinceMs = null;
      }
    }
    this.isPlaying = to === 'playing';
    this.syncActive();

    // Time spent armed before the match went live, closed on the way out of
    // 'waiting' so it never includes the playback that followed.
    if (to === 'waiting') this.waitingSinceMs = t;
    else if (this.waitingSinceMs !== null) {
      this.waitingTotalMs += t - this.waitingSinceMs;
      this.waitingSinceMs = null;
    }

    if (to === 'buffering' && from === 'playing') {
      // Two kinds of stall are not the delivery path's fault and are excluded
      // from every stall figure:
      //
      //   hidden  — a backgrounded tab is throttled into 'buffering'; the
      //             viewer saw nothing.
      //   seek    — the buffering that follows a seek is the seek's. The
      //             state machine is driven by media events that carry no
      //             reason, so this is inferred, and causeOf has always
      //             inferred it for the LABEL; now the counters agree.
      //
      // (A stall that begins visible and later goes hidden is still one
      // stall: the count runs once here; syncBuffering only pauses its time.)
      this.stallIsSeekInduced = this.seekInduced(t);
      const counts = !this.isHidden && !this.stallIsSeekInduced;
      if (counts) this.rebufferCount += 1;
      this.isBuffering = true;
      this.lastStallMs = t;
      // Gated identically: "time to first stall" is a delivery figure, and a
      // seek is not a delivery event. Leaving it ungated would let a session
      // report a first stall it never counted.
      if (counts && this.timeToFirstStallMs === 0) {
        // Playback delivered before the first interruption — a session that
        // stalls at 3s and one that stalls at 30 minutes read identically
        // through the stall count alone.
        this.timeToFirstStallMs = Math.max(MIN_STAGE_MS, Math.round(this.playingTotalMs));
      }
    } else if (from === 'buffering') {
      this.isBuffering = false;
      this.stallIsSeekInduced = false;
    }
    // Open/close the rebuffer window against the new buffering state; visibility
    // changes come through onVisibility, which also calls syncBuffering.
    this.syncBuffering();
    if (to === 'paused') this.pauseCount += 1;
    this.syncIdle(to);

    const change: NonNullable<WireBeacon['stateChange']> = {
      fromState: STATE_TO_WIRE[from] ?? 'PLAYER_STATE_UNSPECIFIED',
      toState: STATE_TO_WIRE[to] ?? 'PLAYER_STATE_UNSPECIFIED',
      presentation: PRESENTATION_TO_WIRE[this.presentation],
    };
    const cause = this.causeOf(from, to, t);
    if (cause) change.cause = cause;
    if (positionMs > 0) change.positionMs = Math.round(positionMs);
    if (renditionHeight) change.renditionHeight = renditionHeight;
    this.enqueue({ stateChange: change });
  }

  /**
   * Was the stall starting at `t` the seek's rather than the network's?
   *
   * ONE definition, shared by the transition cause and by the rebuffer
   * accounting, because they are the same question: if the label said SEEK
   * while the counter said network stall, the beacon would contradict itself
   * inside a single session.
   *
   * The `lastSeekMs >= 0` guard is load-bearing, not defensive. Without it,
   * any stall within the first second of a clock that starts near zero reads
   * as seek-induced — true of an injected `now`, and the first stall of a
   * session is exactly the one a join-time analysis cares about most. The
   * "no seek yet" sentinel is −1 rather than 0 because 0 is a real seek
   * time on such a clock: a seek at the epoch must still own the
   * buffering that follows it.
   */
  private seekInduced(t: number): boolean {
    return this.lastSeekMs >= 0 && t - this.lastSeekMs < SEEK_STALL_WINDOW_MS;
  }

  /**
   * Why a transition happened, as far as the player can tell. Inferred, not
   * reported: the state machine is driven by media events that carry no
   * reason, so buffering right after a seek is the seek's, and buffering
   * otherwise is the network running dry — which is what the state means.
   */
  private causeOf(from: PlayerState, to: PlayerState, t: number): WireCause | undefined {
    if (to === 'error') return 'TRANSITION_CAUSE_ERROR';
    if (to === 'buffering') {
      return this.seekInduced(t) ? 'TRANSITION_CAUSE_SEEK' : 'TRANSITION_CAUSE_NETWORK_STALL';
    }
    if (to === 'paused' || (to === 'playing' && from === 'paused')) {
      return 'TRANSITION_CAUSE_USER';
    }
    return undefined;
  }

  /** Open or close the active-watch window after any of its three inputs moved. */
  private syncActive(at = this.now()): void {
    const on = this.isPlaying && !this.isHidden && !this.isMuted;
    if (on && this.activeSinceMs === null) this.activeSinceMs = at;
    else if (!on && this.activeSinceMs !== null) {
      this.activeTotalMs += at - this.activeSinceMs;
      this.activeSinceMs = null;
    }
  }

  /**
   * Open or close the rebuffer window after buffering or visibility moved.
   * Mirrors syncActive: a hidden tab is throttled into 'buffering', and that
   * stall time is not something the viewer experienced, so rebuffer time
   * accrues only while isBuffering && !isHidden.
   */
  private syncBuffering(): void {
    const on = this.isBuffering && !this.isHidden && !this.stallIsSeekInduced;
    if (on && this.bufferingSinceMs === null) this.bufferingSinceMs = this.now();
    else if (!on && this.bufferingSinceMs !== null) {
      this.rebufferTotalMs += this.now() - this.bufferingSinceMs;
      this.bufferingSinceMs = null;
    }
  }

  /** Bank the time spent in the current presentation and restart the clock. */
  private closePresentation(t = this.now()): void {
    const spent = t - this.presentationSinceMs;
    if (this.presentation === 'fullscreen') this.fullscreenTotalMs += spent;
    else if (this.presentation === 'pip') this.pipTotalMs += spent;
    this.presentationSinceMs = t;
  }

  /**
   * The autoplay verdict, once play() has resolved (or was never attempted).
   * Lands on SessionStart when it arrives inside the first-flush window,
   * which is the normal case; a slow manifest can miss it, and the field is
   * then simply absent rather than wrong.
   */
  noteAutoplay(result: WireAutoplay): void {
    if (this.pendingStart) this.pendingStart.autoplay = result;
  }

  /**
   * Environment facts that only resolve asynchronously (the high-entropy UA
   * client hints). Same one-shot window as the autoplay verdict: patched
   * onto SessionStart while it is still held, ignored once it has gone.
   */
  noteEnvironment(extra: Partial<SessionEnvironment>): void {
    if (this.pendingStart) Object.assign(this.pendingStart, extra);
  }

  /**
   * The audio state a session STARTS in. Counts no change: a player that
   * begins muted (the autoplay-friendly default) has had nothing done to
   * it, and reporting that as a mute change would put a phantom 1 on every
   * session and destroy the "did the viewer touch the volume" signal.
   */
  seedVolume(muted: boolean, volume: number): void {
    if (this.endedFlag) return;
    this.isMuted = muted || volume === 0;
    if (this.isMuted && this.isPlaying && this.mutedSinceMs === null) {
      this.mutedSinceMs = this.now();
    }
    this.syncActive();
  }

  /** Volume or mute changed on the media element. */
  noteVolume(muted: boolean, volume: number): void {
    if (this.endedFlag) return;
    const silent = muted || volume === 0;
    if (silent === this.isMuted) {
      // Same audibility, different level: a volume change, not a mute.
      this.volumeChanges += 1;
      return;
    }
    this.muteChanges += 1;
    const t = this.now();
    if (silent) {
      // Only while playing: a muted pause is not discountable watch time.
      if (this.isPlaying && this.mutedSinceMs === null) this.mutedSinceMs = t;
    } else {
      if (this.mutedSinceMs !== null) {
        this.mutedTotalMs += t - this.mutedSinceMs;
        this.mutedSinceMs = null;
      }
      // Unmuting mid-playback is when a muted-autoplay session first
      // becomes audible — the only moment that stage can be observed for
      // the dominant startup path, which no state change reports.
      if (this.firstAudioMs === 0 && this.isPlaying) {
        this.firstAudioMs = Math.max(MIN_STAGE_MS, t - this.startupAnchorMs);
      }
    }
    this.isMuted = silent;
    this.syncActive();
  }

  /** Fullscreen / picture-in-picture / inline transition. */
  notePresentation(next: Presentation): void {
    if (this.endedFlag || next === this.presentation) return;
    this.closePresentation();
    this.presentation = next;
  }

  /**
   * The viewer asked for playback. Re-anchors the startup ladder here, once.
   *
   * Only the autoplay-off path calls this, and only on the first play(): an
   * autoplay session already has intent at construction, and a SECOND call
   * after playback began would rewrite a finished measurement.
   *
   * A stage that already closed is re-stamped PRE-PAID rather than left
   * alone or allowed to go negative. In the deferred path the engine parses
   * the manifest while fetching no media, so manifestLoadMs is genuinely
   * already done when the viewer clicks — it cost them nothing, and
   * MIN_STAGE_MS is how this contract says "reached, immeasurably fast".
   * Leaving the old value would keep the poster dwell inside the ladder by
   * another route.
   */
  notePlayIntent(): void {
    if (this.endedFlag || this.startupTimeMs > 0) return;
    this.startupAnchorMs = this.now();
    if (this.manifestLoadMs > 0) this.manifestLoadMs = MIN_STAGE_MS;
    if (this.firstFragLoadMs > 0) this.firstFragLoadMs = MIN_STAGE_MS;
  }

  /** Engine 'ready' — closes the manifest-load startup stage. */
  noteReady(): void {
    if (this.manifestLoadMs === 0) {
      this.manifestLoadMs = Math.max(MIN_STAGE_MS, this.now() - this.startupAnchorMs);
    }
  }

  /** First media bytes observed — closes the first-segment startup stage. */
  noteFirstBytes(): void {
    this.sawFirstBytes = true;
    if (this.firstFragLoadMs === 0) {
      this.firstFragLoadMs = Math.max(MIN_STAGE_MS, this.now() - this.startupAnchorMs);
    }
  }

  noteSeek(): void {
    if (this.endedFlag) return;
    this.seekCount += 1;
    // Remembered so the buffering that usually follows is attributed to the
    // seek rather than reported as a network stall.
    this.lastSeekMs = this.now();
  }

  /** Playback error (code from src/core/errors.ts — strings, no lockstep). */
  noteError(
    code: string,
    detail: string | undefined,
    fatal: boolean,
    httpStatus: number,
    positionMs: number,
    /** The ENGINE's own name for the failure, when it supplied one. */
    engineDetail?: string,
  ): void {
    if (this.endedFlag) return;
    this.errorCount += 1;
    this.lastErrorMs = this.now();
    const err: NonNullable<WireBeacon['error']> = { code };
    if (detail) err.detail = detail;
    if (fatal) err.fatal = true;
    if (httpStatus > 0) err.httpStatus = httpStatus;
    if (positionMs > 0) err.positionMs = Math.round(positionMs);
    const stage = this.stageOf(code, detail, engineDetail);
    err.stage = stage;
    this.lastErrorStage = stage;
    const host = this.safeSample()?.cdnHost;
    if (host) err.cdnHost = host;
    this.enqueue({ error: err });
    // Errors shouldn't wait up to 15s to surface fleet-wide.
    this.flush();
  }

  /**
   * Which step of the pipeline an error interrupted. Resolve-time codes come
   * from the playback API before any media is touched; the rest are read off
   * the engine's own detail string, which is why the contract carries the
   * stage as an enum instead of leaving every reader to parse that string.
   */
  private stageOf(
    code: string,
    detail: string | undefined,
    engineDetail: string | undefined,
  ): WireErrorStage {
    // The ENGINE's own name for the failure when it gave one. The player's
    // message is composed prose — "NETWORK error: fragLoadError (...)" —
    // and staging by pattern-matching that prose is how the single most
    // common fatal error, a fragment load giving up, was staged as OTHER:
    // no anchored pattern can match a string that opens with the error
    // KIND. It also settles DRM: a licence 401 arrives with the code
    // UNAUTHORIZED, which is indistinguishable from a resolve failure by
    // code alone, but the engine calls it keyLoadError.
    const engine = engineDetail ?? '';
    if (engine !== '') {
      // fragLOAD only: a fragment that arrived and would not parse
      // (fragParsingError, fragDecryptError) is a decode failure, not a
      // delivery one, and staging it as a segment problem would send
      // someone to look at the CDN for a codec bug.
      if (/^fragLoad/i.test(engine)) {
        return this.sawFirstBytes ? 'ERROR_STAGE_SEGMENT' : 'ERROR_STAGE_FIRST_SEGMENT';
      }
      if (/^(manifest|level|audioTrack|subtitleTrack)/i.test(engine)) {
        return 'ERROR_STAGE_MANIFEST';
      }
      if (/key|licen[cs]e/i.test(engine)) return 'ERROR_STAGE_DRM';
      if (/parsing|append|buffer|remux|decrypt|media/i.test(engine)) return 'ERROR_STAGE_DECODE';
      return 'ERROR_STAGE_OTHER';
    }

    // No engine detail: the failure did not come from a running engine, so
    // the resolve stage is reachable here and nowhere else.
    if (code === 'DRM_CLIENT') return 'ERROR_STAGE_DRM';
    if (RESOLVE_CODES.has(code)) return 'ERROR_STAGE_RESOLVE';
    const d = detail ?? '';
    if (/frag|segment/i.test(d)) {
      return this.sawFirstBytes ? 'ERROR_STAGE_SEGMENT' : 'ERROR_STAGE_FIRST_SEGMENT';
    }
    if (/manifest|level|playlist/i.test(d)) return 'ERROR_STAGE_MANIFEST';
    if (/key|licen[cs]e/i.test(d)) return 'ERROR_STAGE_DRM';
    if (/buffer|decode|media|append/i.test(d)) return 'ERROR_STAGE_DECODE';
    return 'ERROR_STAGE_OTHER';
  }

  /** The 'ended' player state maps here with reason ENDED. */
  noteEnded(): void {
    this.end('END_REASON_ENDED');
  }

  /** Player torn down by the embedding page. */
  noteDestroyed(): void {
    this.end('END_REASON_STOPPED');
  }

  /** Fatal error path. */
  noteFatal(): void {
    this.end('END_REASON_FATAL_ERROR');
  }

  private heartbeat(): void {
    if (this.endedFlag) return;
    const t = this.now();
    // The first tick after a suspension ends the session instead of
    // reporting an interval that spans the whole gap.
    if (this.checkSuspension(t)) return;
    const s = this.opts.sample();

    // Close open accounting windows into the totals without ending them.
    let playing = this.playingTotalMs;
    if (this.playingSinceMs !== null) playing += t - this.playingSinceMs;
    let hidden = this.hiddenTotalMs;
    if (this.hiddenSinceMs !== null) hidden += t - this.hiddenSinceMs;
    // Stall, per interval. bufferingSinceMs is opened and closed by
    // syncBuffering, which holds it ONLY while the stall is visible and not
    // seek-induced — so both exclusions come for free here and, more to the
    // point, cannot drift from the session totals that share that window.
    // A stall spanning two heartbeats is split across them and counted once.
    let rebuffer = this.rebufferTotalMs;
    if (this.bufferingSinceMs !== null) rebuffer += t - this.bufferingSinceMs;
    // Active watch (playing AND visible AND unmuted), per interval, off the
    // window syncActive already maintains. MEASURED, not derived: the server
    // has been reconstructing this as playing - hidden - muted, and those
    // three overlap, so an interval that was both hidden and muted is
    // subtracted twice. The overlap is not recoverable after the fact, which
    // is why it has to be observed here rather than computed there.
    let active = this.activeTotalMs;
    if (this.activeSinceMs !== null) active += t - this.activeSinceMs;

    const hb: Record<string, number | string | boolean> = {};
    const bytes = s.bytesLoaded ?? 0;
    if (bytes > this.lastBytes) hb.bytesDownloadedDelta = bytes - this.lastBytes;
    this.lastBytes = bytes;
    const pl = s.playlistBytesLoaded ?? 0;
    if (pl > this.lastPlaylistBytes) hb.playlistBytesDelta = pl - this.lastPlaylistBytes;
    this.lastPlaylistBytes = pl;
    const dropped = s.droppedFrames ?? 0;
    if (dropped > this.lastDropped) hb.droppedFramesDelta = dropped - this.lastDropped;
    this.lastDropped = dropped;
    const switches = s.renditionSwitches ?? 0;
    if (switches > this.lastSwitches) hb.renditionSwitchesDelta = switches - this.lastSwitches;
    this.lastSwitches = switches;
    if (playing > this.lastHbPlayingMs)
      hb.playingMsDelta = Math.round(playing - this.lastHbPlayingMs);
    this.lastHbPlayingMs = playing;
    if (hidden > this.lastHbHiddenMs) hb.hiddenMsDelta = Math.round(hidden - this.lastHbHiddenMs);
    this.lastHbHiddenMs = hidden;
    if (rebuffer > this.lastHbRebufferMs) {
      hb.rebufferMsDelta = Math.round(rebuffer - this.lastHbRebufferMs);
    }
    this.lastHbRebufferMs = rebuffer;
    // The COUNT is already gated on the same two exclusions where it is
    // incremented, so this is a plain watermark diff. A stall that opens in
    // one interval and closes in a later one counts in the interval it
    // OPENED, which is where the viewer felt it.
    if (this.rebufferCount > this.lastHbRebufferCount) {
      hb.rebufferCountDelta = this.rebufferCount - this.lastHbRebufferCount;
    }
    this.lastHbRebufferCount = this.rebufferCount;
    // Deltas cover FULL heartbeat intervals only — end() emits no final
    // heartbeat, so a sub-interval tail after the last tick is carried by
    // the sessionEnd totals alone. sum(activeMsDelta) therefore reconciles
    // with activeWatchTimeMs through the last tick; the remainder is always
    // < heartbeatMs. Same policy as every other per-interval figure above.
    if (active > this.lastHbActiveMs) hb.activeMsDelta = Math.round(active - this.lastHbActiveMs);
    this.lastHbActiveMs = active;

    if (typeof s.latencySeconds === 'number') hb.latencyMs = Math.round(s.latencySeconds * 1000);
    if (typeof s.targetLatencySeconds === 'number') {
      hb.targetLatencyMs = Math.round(s.targetLatencySeconds * 1000);
    }
    if (typeof s.bandwidthKbps === 'number') hb.bandwidthEstKbps = s.bandwidthKbps;
    if (typeof s.levelHeight === 'number') hb.renditionHeight = s.levelHeight;
    if (typeof s.renditionBitrateKbps === 'number') {
      hb.renditionBitrateKbps = s.renditionBitrateKbps;
    }
    if (s.atLiveEdge === true) hb.atLiveEdge = true;
    if (typeof s.bufferedMs === 'number' && s.bufferedMs > 0) {
      hb.bufferedMs = Math.round(s.bufferedMs);
    }
    hb.playerState = STATE_TO_WIRE[s.state] ?? 'PLAYER_STATE_UNSPECIFIED';

    // ---- Contract v2.
    let muted = this.mutedTotalMs;
    if (this.mutedSinceMs !== null) muted += t - this.mutedSinceMs;
    if (muted > this.lastHbMutedMs) hb.mutedMsDelta = Math.round(muted - this.lastHbMutedMs);
    this.lastHbMutedMs = muted;

    const up = s.upshifts ?? 0;
    if (up > this.lastUpshifts) hb.upshiftsDelta = up - this.lastUpshifts;
    this.lastUpshifts = up;
    const down = s.downshifts ?? 0;
    if (down > this.lastDownshifts) hb.downshiftsDelta = down - this.lastDownshifts;
    this.lastDownshifts = down;
    const decoded = s.decodedFrames ?? 0;
    if (decoded > this.lastDecoded) hb.decodedFramesDelta = decoded - this.lastDecoded;
    this.lastDecoded = decoded;
    const reqErrors = s.requestErrors ?? 0;
    if (reqErrors > this.lastRequestErrors) {
      hb.requestErrorsDelta = reqErrors - this.lastRequestErrors;
    }
    this.lastRequestErrors = reqErrors;

    // Request timing: the engine keeps a ring of recent durations and a
    // cumulative count, so the tail this interval added is exactly the
    // slice to summarize — no per-request beacon, no unbounded array.
    const reqCount = s.requestCount ?? 0;
    const reqDelta = reqCount - this.lastRequestCount;
    this.lastRequestCount = reqCount;
    if (reqDelta > 0) {
      hb.requestCountDelta = reqDelta;
      const times = s.requestTimesMs ?? [];
      const recent = times.slice(Math.max(0, times.length - reqDelta)).sort((a, b) => a - b);
      if (recent.length > 0) {
        hb.requestTimeP50Ms = percentile(recent, 50);
        hb.requestTimeP95Ms = percentile(recent, 95);
      }
    }

    if (typeof s.latencySeconds === 'number' && typeof s.targetLatencySeconds === 'number') {
      // Signed: behind the target is positive, ahead of it negative.
      hb.driftMs = Math.round((s.latencySeconds - s.targetLatencySeconds) * 1000);
    }
    if (typeof s.playbackRate === 'number' && s.playbackRate > 0) {
      hb.playbackRate = s.playbackRate;
    }
    if (s.qualityPinned === true) hb.qualityPinned = true;
    if (s.cdnHost) hb.cdnHost = s.cdnHost;

    this.maybeEmitMediaInfo(s);
    this.enqueue({ heartbeat: hb });
    this.flush();

    if (bytes > 0) this.noteFirstBytes();
  }

  /**
   * MediaInfo once per session, as soon as the engine has negotiated what it
   * will play. SessionStart cannot carry this: it is emitted at engine
   * selection, before the manifest is parsed or a DRM session exists.
   */
  private maybeEmitMediaInfo(s: AnalyticsSample): void {
    const known = s.videoCodec ?? s.keySystem ?? s.ladderHeights;
    if (!known) return;
    const info: NonNullable<WireBeacon['mediaInfo']> = {};
    if (s.videoCodec) info.videoCodec = s.videoCodec;
    if (s.audioCodec) info.audioCodec = s.audioCodec;
    if (s.keySystem) info.keySystem = KEY_SYSTEM_TO_WIRE[s.keySystem];
    if (s.drmSecurityLevel) info.drmSecurityLevel = s.drmSecurityLevel;
    if (s.ladderHeights?.length) info.ladderHeights = s.ladderHeights;
    if (s.ladderTopBitrateKbps) info.ladderTopBitrateKbps = s.ladderTopBitrateKbps;
    if (s.lowLatency) info.lowLatency = true;
    // The contract says MediaInfo is emitted again if the negotiated facts
    // change (an audio-track switch changes the codec; a rebuilt engine can
    // change the ladder), so this compares rather than firing once. A
    // session whose media never changes still sends exactly one.
    const signature = JSON.stringify(info);
    if (signature === this.mediaInfoSignature) return;
    this.mediaInfoSignature = signature;
    this.enqueue({ mediaInfo: info });
  }

  /**
   * End the session exactly once: totals beacon + final flush. On pagehide
   * the flush rides navigator.sendBeacon — the only delivery a closing page
   * guarantees.
   */
  end(reason: WireEndReason, at?: number): void {
    if (this.endedFlag) return;
    this.endedFlag = true;
    // `at`: the instant the session is closed AS OF. Only the suspension
    // path passes one — the last tick before the page stopped — so a slept
    // laptop closes every open window (playing, active, muted, hidden) there
    // and books none of the sleep.
    const t = at ?? this.now();
    if (this.playingSinceMs !== null) {
      this.playingTotalMs += t - this.playingSinceMs;
      this.playingSinceMs = null;
    }
    if (this.hiddenSinceMs !== null) {
      this.hiddenTotalMs += t - this.hiddenSinceMs;
      this.hiddenSinceMs = null;
    }
    if (this.bufferingSinceMs !== null) {
      this.rebufferTotalMs += t - this.bufferingSinceMs;
      this.bufferingSinceMs = null;
    }
    if (this.mutedSinceMs !== null) {
      this.mutedTotalMs += t - this.mutedSinceMs;
      this.mutedSinceMs = null;
    }
    if (this.waitingSinceMs !== null) {
      this.waitingTotalMs += t - this.waitingSinceMs;
      this.waitingSinceMs = null;
    }
    this.isPlaying = false;
    // At `t`, like every window above: the active-watch and presentation
    // windows are the two that read the clock themselves.
    this.syncActive(t);
    this.closePresentation(t);
    if (this.firstFlushTimer) {
      clearTimeout(this.firstFlushTimer);
      this.firstFlushTimer = null;
    }
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.onPagehide);
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('freeze', this.onFreeze);
      document.removeEventListener('resume', this.onResume);
    }

    const s = this.safeSample();
    // A session that ends before its first heartbeat — an early error, a
    // quick bounce, a short preview — would otherwise never report what it
    // was playing, even though the engine has known since the manifest
    // parsed.
    if (s) this.maybeEmitMediaInfo(s);
    const endTotals: Record<string, number | string> = {
      reason,
    };
    const bytes = s?.bytesLoaded ?? this.lastBytes;
    if (bytes > 0) endTotals.bytesDownloadedTotal = bytes;
    if (this.playingTotalMs > 0) endTotals.watchTimeMs = Math.round(this.playingTotalMs);
    if (this.rebufferCount > 0) endTotals.rebufferCount = this.rebufferCount;
    if (this.rebufferTotalMs > 0) endTotals.rebufferTimeMs = Math.round(this.rebufferTotalMs);
    if (this.startupTimeMs > 0) endTotals.startupTimeMs = Math.round(this.startupTimeMs);
    if (this.manifestLoadMs > 0) endTotals.manifestLoadMs = Math.round(this.manifestLoadMs);
    if (this.firstFragLoadMs > 0) endTotals.firstFragLoadMs = Math.round(this.firstFragLoadMs);
    const license = s?.licenseTimeMs ?? 0;
    if (license > 0) endTotals.licenseTimeMs = Math.round(license);
    const dropped = s?.droppedFrames ?? this.lastDropped;
    if (dropped > 0) endTotals.droppedFramesTotal = dropped;
    if (this.errorCount > 0) endTotals.errorCount = this.errorCount;
    if (this.hiddenTotalMs > 0) endTotals.hiddenTimeMs = Math.round(this.hiddenTotalMs);
    if (this.seekCount > 0) endTotals.seekCount = this.seekCount;
    if (this.pauseCount > 0) endTotals.pauseCount = this.pauseCount;

    // ---- Contract v2 totals.
    if (this.firstAudioMs > 0) endTotals.firstAudioMs = Math.round(this.firstAudioMs);
    if (this.waitingTotalMs > 0) endTotals.waitingForLiveMs = Math.round(this.waitingTotalMs);
    if (this.timeToFirstStallMs > 0) {
      endTotals.timeToFirstStallMs = Math.round(this.timeToFirstStallMs);
    }
    const exit = s?.positionMs ?? 0;
    if (exit > 0) endTotals.exitPositionMs = Math.round(exit);
    if (this.activeTotalMs > 0) endTotals.activeWatchTimeMs = Math.round(this.activeTotalMs);
    if (this.mutedTotalMs > 0) endTotals.mutedTimeMs = Math.round(this.mutedTotalMs);
    // Abandon cohorts: only when the session actually ended NEAR the event,
    // so an unrelated stall an hour earlier never reads as an abandonment.
    // Not on a clean end: a viewer who stalled, recovered and watched to
    // the finish has not abandoned anything.
    const abandoned = reason !== 'END_REASON_ENDED';
    if (abandoned && this.lastStallMs > 0 && t - this.lastStallMs <= ABANDON_WINDOW_MS) {
      endTotals.abandonAfterStallMs = Math.max(MIN_STAGE_MS, Math.round(t - this.lastStallMs));
    }
    if (abandoned && this.lastErrorMs > 0 && t - this.lastErrorMs <= ABANDON_WINDOW_MS) {
      endTotals.abandonAfterErrorMs = Math.max(MIN_STAGE_MS, Math.round(t - this.lastErrorMs));
    }
    if (this.fullscreenTotalMs > 0) {
      endTotals.fullscreenTimeMs = Math.round(this.fullscreenTotalMs);
    }
    if (this.pipTotalMs > 0) endTotals.pipTimeMs = Math.round(this.pipTotalMs);
    if (this.volumeChanges > 0) endTotals.volumeChanges = this.volumeChanges;
    if (this.muteChanges > 0) endTotals.muteChanges = this.muteChanges;
    const decodedTotal = s?.decodedFrames ?? this.lastDecoded;
    if (decodedTotal > 0) endTotals.decodedFramesTotal = decodedTotal;
    // Only for a session that never played: which stage it died at. The last
    // error's stage when there was one, otherwise the first startup stage
    // that never closed.
    if (this.startupTimeMs === 0) {
      endTotals.failedStage =
        this.lastErrorStage ??
        (this.manifestLoadMs === 0
          ? 'ERROR_STAGE_MANIFEST'
          : this.firstFragLoadMs === 0
            ? 'ERROR_STAGE_FIRST_SEGMENT'
            : 'ERROR_STAGE_OTHER');
    }

    this.pending.push(this.wrap({ sessionEnd: endTotals }));
    this.flush(reason === 'END_REASON_PAGEHIDE');
  }

  private safeSample(): AnalyticsSample | null {
    try {
      return this.opts.sample();
    } catch {
      return null; // engine already torn down — totals fall back to last-seen
    }
  }

  private wrap(event: Omit<WireBeacon, 'sid' | 'clientTime' | 'seq'>): WireBeacon {
    this.seq += 1;
    return {
      sid: this.opts.sid,
      clientTime: new Date(this.now()).toISOString(),
      seq: this.seq,
      ...event,
    };
  }

  private enqueue(event: Omit<WireBeacon, 'sid' | 'clientTime' | 'seq'>): void {
    this.pending.push(this.wrap(event));
    while (this.pending.length > MAX_PENDING) {
      // Evict the oldest routine beacon (heartbeat/state change) first;
      // lifecycle and error beacons carry the session's story and only go
      // when nothing routine is left to shed.
      const i = this.pending.findIndex((b) => b.heartbeat || b.stateChange);
      this.pending.splice(i >= 0 ? i : 0, 1);
    }
  }

  private flush(useSendBeacon = false): void {
    if (this.firstFlushTimer) {
      clearTimeout(this.firstFlushTimer);
      this.firstFlushTimer = null;
    }
    this.pendingStart = null;
    if (this.pending.length === 0) return;
    // The batch carries the beacons and nothing else. No api key rides a
    // beacon in any form — not as a header, not in the envelope: the
    // ingest has no origin gate to resolve it against (the origin was
    // enforced by havik-streams when it minted the sid, and the ingest's
    // reads join the mints), and a publishable key is never an identity.
    // What identifies the batch is the sid on every beacon.
    const body = JSON.stringify({ beacons: this.pending });
    this.pending = [];

    if (useSendBeacon) {
      // Deliberately a STRING body: sendBeacon with a string posts
      // text/plain, which is CORS-safelisted — no preflight, and a
      // preflight never completes on an unloading page. A Blob typed
      // application/json would trigger one and lose the final batch. The
      // ingest parses the body regardless of Content-Type by design (D9).
      const send =
        this.opts.sendBeacon ??
        (typeof navigator !== 'undefined' && navigator.sendBeacon
          ? navigator.sendBeacon.bind(navigator)
          : undefined);
      if (send && send(this.url, body)) return;
      // sendBeacon refused (quota) — fall through to keepalive fetch, which
      // may or may not complete on an unloading page; acceptable loss.
    }
    const doFetch = this.opts.fetchFn ?? (typeof fetch !== 'undefined' ? fetch : undefined);
    if (!doFetch) return;
    void doFetch(this.url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      // Fire-and-forget: a failed batch is dropped by design.
    });
  }
}
